/**
 * Conversation Helper
 * Common functions for chat panels with speech recognition and text-to-speech
 */

/**
 * Initialize Speech Recognition with proper language and settings
 * @param {string} language - Language code ('es' or 'en')
 * @returns {SpeechRecognition|null} - Recognition instance or null if not supported
 */
export const initSpeechRecognition = (language) => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    console.warn("Speech Recognition not supported in this browser");
    return null;
  }

  const recognition = new SpeechRecognition();
  recognition.continuous = true; // Keep listening until user stops
  recognition.interimResults = true; // Show interim results for better UX
  recognition.lang = language === "es" ? "es-ES" : "en-US";

  return recognition;
};

/**
 * Setup Speech Recognition event handlers
 * @param {SpeechRecognition} recognition - Recognition instance
 * @param {Object} handlers - Event handler callbacks
 * @param {Function} handlers.onStart - Called when recognition starts
 * @param {Function} handlers.onResult - Called with transcript results {final: string, interim: string}
 * @param {Function} handlers.onEnd - Called when recognition ends
 * @param {Function} handlers.onError - Called with error event
 * @param {Object} refs - References object
 * @param {React.MutableRefObject} refs.manualListeningRef - Ref to track manual listening state
 */
export const setupSpeechRecognitionHandlers = (
  recognition,
  handlers,
  refs
) => {
  const { onStart, onResult, onEnd, onError } = handlers;
  const { manualListeningRef } = refs;

  recognition.onstart = () => {
    if (onStart) onStart();
  };

  recognition.onresult = (event) => {
    let finalTranscript = "";
    let interim = "";

    // Use resultIndex to only process NEW results (avoid duplicates in continuous mode)
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const text = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += text;
      } else {
        interim += text;
      }
    }

    if (onResult) {
      onResult({ final: finalTranscript.trim(), interim });
    }
  };

  recognition.onend = () => {
    // If the user explicitly started listening and didn't stop, try to restart
    if (manualListeningRef.current) {
      try {
        recognition.start();
      } catch (e) {
        console.warn("Could not restart recognition:", e);
      }
    }
    if (onEnd) onEnd();
  };

  recognition.onerror = (event) => {
    console.error("Speech recognition error:", event.error);
    
    // For critical errors like "not-allowed", stop manual mode
    if (event.error === "not-allowed" || event.error === "service-not-allowed") {
      manualListeningRef.current = false;
    }
    
    if (onError) onError(event);
  };
};

/**
 * Start Speech Recognition manually
 * @param {SpeechRecognition} recognition - Recognition instance
 * @param {React.MutableRefObject} manualListeningRef - Ref to track manual listening
 * @param {Function} setIsListening - State setter for listening status
 * @returns {boolean} - Success status
 */
export const startSpeechRecognition = (
  recognition,
  manualListeningRef,
  setIsListening
) => {
  if (!recognition) {
    console.warn("Speech recognition not available");
    return false;
  }

  manualListeningRef.current = true;

  try {
    recognition.start();
    setIsListening(true);
    return true;
  } catch (e) {
    console.error("Failed to start recognition:", e);
    return false;
  }
};

/**
 * Stop Speech Recognition manually
 * @param {SpeechRecognition} recognition - Recognition instance
 * @param {React.MutableRefObject} manualListeningRef - Ref to track manual listening
 * @param {Function} setIsListening - State setter for listening status
 * @param {Function} setInterimTranscript - State setter for interim transcript
 */
export const stopSpeechRecognition = (
  recognition,
  manualListeningRef,
  setIsListening,
  setInterimTranscript
) => {
  // Mark that the user explicitly stopped listening so we don't auto-restart
  manualListeningRef.current = false;
  
  try {
    if (recognition) {
      recognition.stop();
    }
  } catch (e) {
    console.warn("Error stopping recognition:", e);
  }
  
  setIsListening(false);
  setInterimTranscript("");
};

/**
 * Text-to-Speech function
 * @param {string} text - Text to speak
 * @param {string} lang - Language code ('es' or 'en')
 */
export const speak = (text, lang = "en") => {
  if (!window.speechSynthesis || !text) return;
  
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang === "es" ? "es-ES" : "en-US";
  utterance.rate = 1;
  utterance.pitch = 1;
  
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};

/**
 * Stop Text-to-Speech
 */
export const stopSpeaking = () => {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

/**
 * Auto-resize textarea to fit content
 * @param {React.RefObject} inputRef - Ref to textarea element
 */
export const autoResizeTextarea = (inputRef) => {
  const el = inputRef.current;
  if (el) {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    // Enforce a max height equivalent to ~8 lines (approx 160px for 20px line-height)
    if (el.scrollHeight > 160) {
      el.style.height = "160px";
      el.style.overflowY = "auto";
    } else {
      el.style.overflowY = "hidden";
    }
  }
};

/**
 * Handle Enter key to send message (Shift+Enter for newline)
 * @param {KeyboardEvent} e - Keyboard event
 * @param {Function} sendCallback - Callback to send message
 */
export const handleEnterKey = (e, sendCallback) => {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    sendCallback();
  }
};

/**
 * Scroll to bottom of message container
 * @param {React.RefObject} messagesEndRef - Ref to bottom element
 * @param {Object} options - Scroll options
 */
export const scrollToBottom = (messagesEndRef, options = {}) => {
  const defaultOptions = {
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  };
  
  messagesEndRef.current?.scrollIntoView({ ...defaultOptions, ...options });
};
